import React, { useState } from "react";
import { Plus } from "lucide-react";
import { z } from "zod/v3";

import { genid } from "@workspace/shared/utils";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { ScrollArea } from "@workspace/ui/components/scroll-area";
import { Spinner } from "@workspace/ui/components/spinner";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";
import { Textarea } from "@workspace/ui/components/textarea";
import { cn } from "@workspace/ui/lib/utils";

import type { TabInput } from "../../types";
import type { EposTab } from "../epos-context";
import { useEposContext } from "../epos-context";
import { CountInput } from "./count-input";

const TabInputSchema = z.object({
	tabId: z.string().min(1),
	number: z.number().int().min(1),
	name: z.string().max(50),
	section: z.string().min(1),
	covers: z
		.number()
		.int()
		.min(1, "Covers must be at least 1")
		.max(999, "Maximum 999 covers"),
	notes: z.string().max(200, "Maximum 200 characters"),
	createdAt: z.string(),
});

type TabFormValues = z.infer<typeof TabInputSchema>;

function getTodayTabs(
	tabs: { number: number; createdAt?: string | null }[],
	timeZone: string,
) {
	const todayStr = new Date().toLocaleDateString("en-CA", { timeZone });
	return tabs.filter((t) => {
		if (!t.createdAt) return false;
		const tabDate = new Date(t.createdAt).toLocaleDateString("en-CA", {
			timeZone,
		});
		return tabDate === todayStr;
	});
}

export function AddOrderToTabDialog({
	isOpen,
	onOpenChange,
	tabs,
	selectedTabId,
	onSelectTab,
	onConfirm,
	onCreateAndAssign,
	isCreating,
}: {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	tabs: EposTab[];
	selectedTabId: string | null;
	onSelectTab: (tabId: string) => void;
	onConfirm: () => void;
	onCreateAndAssign: (tab: TabInput) => Promise<void>;
	isCreating: boolean;
}) {
	const { timeZone } = useEposContext();
	const [mode, setMode] = useState<"select" | "create">("select");

	const eligibleTabs = React.useMemo(
		// HA-554: every open tab, whether or not it already holds orders (AC1).
		() => tabs.filter((t) => t.status === "Open"),
		[tabs],
	);

	const nextNumber = React.useMemo(() => {
		const todayTabs = getTodayTabs(tabs, timeZone);
		if (todayTabs.length === 0) return 1;
		return Math.max(...todayTabs.map((t) => t.number)) + 1;
	}, [tabs, timeZone]);

	const form = useAppForm({
		defaultValues: {
			tabId: genid(),
			number: nextNumber,
			name: "",
			section: "default",
			covers: 1,
			notes: "",
			createdAt: new Date().toISOString(),
		} satisfies TabFormValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: TabInputSchema,
		},
		onSubmit: async ({ value }) => {
			const payload: TabInput = {
				...value,
				name: value.name || null,
				section: "default",
				tabId: value.tabId,
				number: value.number,
				covers: value.covers,
				createdAt: value.createdAt || new Date().toISOString(),
				notes: value.notes || null,
			};
			await onCreateAndAssign(payload);
		},
	});

	const handleOpenChange = React.useCallback(
		(open: boolean) => {
			if (!open) {
				setMode("select");
				form.reset();
			}
			onOpenChange(open);
		},
		[form, onOpenChange],
	);

	const handleSwitchToCreate = () => {
		form.reset();
		form.setFieldValue("tabId", genid());
		form.setFieldValue("number", nextNumber);
		form.setFieldValue("section", "default");
		form.setFieldValue("covers", 1);
		form.setFieldValue("name", "");
		form.setFieldValue("notes", "");
		form.setFieldValue("createdAt", new Date().toISOString());
		setMode("create");
	};

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-[520px]">
				{mode === "select" ? (
					<>
						<DialogHeader>
							<DialogTitle>Add Order To Tab</DialogTitle>
							<DialogDescription>
								Select an existing tab to attach this order.
							</DialogDescription>
						</DialogHeader>

						{eligibleTabs.length === 0 ? (
							<div className="text-sm text-muted-foreground">
								No open tabs are available.
							</div>
						) : (
							<ScrollArea className="max-h-[340px] pr-2">
								<div className="space-y-2">
									{eligibleTabs.map((tab) => (
										<button
											key={tab.id}
											type="button"
											className={cn(
												"w-full rounded-md border px-3 py-3 text-left hover:bg-muted/50",
												selectedTabId === tab.id && "border-primary",
											)}
											onClick={() => onSelectTab(tab.id)}
										>
											<div className="flex items-center justify-between gap-2">
												<div className="font-medium truncate">
													{tab.name || `#${tab.number}`}
												</div>
												<Badge variant="outline">#{tab.number}</Badge>
											</div>
										</button>
									))}
								</div>
							</ScrollArea>
						)}

						<Button
							type="button"
							variant="outline"
							className="w-full border-dashed"
							onClick={handleSwitchToCreate}
						>
							<Plus className="mr-2 size-4" />
							Create New Tab
						</Button>

						<div className="flex justify-end gap-2 pt-2">
							<Button
								type="button"
								variant="outline"
								onClick={() => handleOpenChange(false)}
							>
								Cancel
							</Button>
							<Button
								type="button"
								onClick={onConfirm}
								disabled={!selectedTabId}
							>
								Add
							</Button>
						</div>
					</>
				) : (
					<>
						<DialogHeader>
							<DialogTitle>Create New Tab</DialogTitle>
							<DialogDescription>
								Create a new tab and assign this order to it.
							</DialogDescription>
						</DialogHeader>

						<form
							onSubmit={(e) => {
								e.preventDefault();
								e.stopPropagation();
								form.handleSubmit();
							}}
							className="space-y-4"
						>
							<form.AppField name="tabId">
								{(field) => (
									<field.FormItem className="hidden">
										<field.FormLabel>Tab Id</field.FormLabel>
										<field.FormControl>
											<Input
												value={field.state.value || ""}
												onChange={(e) => field.handleChange(e.target.value)}
											/>
										</field.FormControl>
										<field.FormMessage />
									</field.FormItem>
								)}
							</form.AppField>

							<form.AppField name="section">
								{(field) => (
									<field.FormItem className="hidden">
										<field.FormLabel>Section</field.FormLabel>
										<field.FormControl>
											<Input
												value={field.state.value || "default"}
												onChange={(e) => field.handleChange(e.target.value)}
												disabled
											/>
										</field.FormControl>
										<field.FormMessage />
									</field.FormItem>
								)}
							</form.AppField>

							<form.AppField name="createdAt">
								{(field) => (
									<field.FormItem className="hidden">
										<field.FormLabel>Created At</field.FormLabel>
										<field.FormControl>
											<Input
												value={field.state.value || ""}
												onChange={(e) => field.handleChange(e.target.value)}
											/>
										</field.FormControl>
										<field.FormMessage />
									</field.FormItem>
								)}
							</form.AppField>

							<form.AppField name="number">
								{(field) => (
									<field.FormItem className="hidden">
										<field.FormControl>
											<Input
												type="number"
												value={field.state.value}
												onChange={(e) =>
													field.handleChange(Number(e.target.value))
												}
												disabled
											/>
										</field.FormControl>
									</field.FormItem>
								)}
							</form.AppField>

							<form.AppField name="name">
								{(field) => (
									<field.FormItem>
										<field.FormLabel>Tab name</field.FormLabel>
										<field.FormControl>
											<Input
												placeholder="e.g. Table 12 (optional)"
												value={field.state.value || ""}
												onChange={(e) => field.handleChange(e.target.value)}
												maxLength={50}
											/>
										</field.FormControl>
										<field.FormMessage />
									</field.FormItem>
								)}
							</form.AppField>

							<form.AppField name="covers">
								{(field) => (
									<field.FormItem>
										<field.FormLabel>Covers</field.FormLabel>
										<field.FormControl>
											<CountInput
												min={1}
												max={999}
												value={field.state.value}
												onValueChange={field.handleChange}
											/>
										</field.FormControl>
										<field.FormMessage />
									</field.FormItem>
								)}
							</form.AppField>

							<form.AppField name="notes">
								{(field) => (
									<field.FormItem>
										<field.FormLabel>Notes</field.FormLabel>
										<field.FormControl>
											<Textarea
												placeholder="Optional notes"
												value={field.state.value || ""}
												onChange={(e) => field.handleChange(e.target.value)}
												maxLength={200}
											/>
										</field.FormControl>
										<field.FormMessage />
									</field.FormItem>
								)}
							</form.AppField>

							<div className="flex justify-end gap-2 pt-2">
								<Button
									type="button"
									variant="outline"
									onClick={() => setMode("select")}
									disabled={isCreating}
								>
									Back
								</Button>
								<Button type="submit" disabled={isCreating}>
									{isCreating && <Spinner />}
									Create & Add
								</Button>
							</div>
						</form>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}
